/*
  Editorial carousel.

  The carousel itself is Dawn's <slider-component>, unmodified. This element
  exists for the things the slider does not do: deciding which card is the
  visible one, and playing only that card.

  Two ways a card plays, and they are different playback, not the same thing at
  different volumes.

  Ambient is the centred card playing on its own: muted, looping, no controls.
  Muted is not a preference, it is the only thing a browser will autoplay, and
  chrome on a looping clip reads as a stalled video rather than as motion.

  Deliberate is the play button: sound on, controls on, no loop. Once a visitor
  has started a card that way it keeps the page for as long as it is mounted -
  autoplay does not quietly take it back while somebody is watching.

  Same discipline as hero-slideshow throughout. Nothing is an autoplay attribute
  in the markup, so a card that has never been centred has never started
  decoding, a hidden tab tears playback down, and prefers-reduced-motion
  suppresses autoplay outright while leaving the buttons working.
*/
class EditorialCarousel extends HTMLElement {
  connectedCallback() {
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    /*
      Both start null and are only ever set by mountFacade. `ambient` is the
      card autoplay chose; `deliberate` is a card the visitor pressed play on.
      A facade can be neither, and never both.
    */
    this.ambient = null;
    this.deliberate = null;

    /*
      True until the viewport observer says otherwise. If IntersectionObserver
      is missing the observer never corrects it, which is the right fallback:
      the section behaves as it did before the observer existed rather than
      never playing anything.
    */
    this.inViewport = true;

    this.track = this.querySelector('[id^="Slider-"]');

    this.setupFacades();
    this.watchViewport();
    this.setupNativeVideos();
    this.bindAutoplayTriggers();

    this.onVisibility = () => {
      if (document.hidden) {
        this.videos.forEach((video) => video.pause());
        /*
          A hidden tab tears the embed down rather than pausing it. There is no
          pause to call on a cross-origin iframe without the provider's JS API,
          so the only way to stop a background tab decoding video is to remove
          it. It comes back on return through syncAutoplay.
        */
        this.unmountAmbient();
      } else {
        this.syncAutoplay();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    this.onMotionChange = () => {
      if (this.reducedMotion.matches) {
        this.videos.forEach((video) => video.pause());
        this.unmountAmbient();
      } else {
        this.syncAutoplay();
      }
    };
    this.reducedMotion.addEventListener('change', this.onMotionChange);

    /*
      No initial sync here. The viewport observer fires on observe with the
      section's current state, and that callback is what starts playback, so
      syncing now would mount for a frame on a section that is far below the
      fold. Only the no-IntersectionObserver fallback needs the nudge.
    */
    if (!('IntersectionObserver' in window)) this.syncAutoplay();
  }

  /* Autoplay -------------------------------------------------------------- */

  /*
    Every condition that has to hold for a card to be playing on its own, in one
    place, so the triggers below can all ask the same question.
  */
  get autoplayEnabled() {
    return (
      this.dataset.autoplay === 'true' &&
      this.facades.length > 0 &&
      this.inViewport &&
      !this.reducedMotion.matches &&
      !document.hidden
    );
  }

  /*
    The active card is the one whose centre is nearest the centre of the track's
    visible box, not the first one intersecting it.

    Nearest-centre is what matches the eye at every width without a second rule
    for mobile: at 1400 five cards are in view and the middle one wins, at 375
    one card fills the track and wins by default. An intersection threshold
    would need different numbers at each width and would still be ambiguous when
    two cards are equally visible.

    Cards entirely outside the track's box are skipped so a carousel scrolled
    to one end does not pick something off screen.
  */
  activeFacade() {
    if (!this.track) return null;

    const box = this.track.getBoundingClientRect();
    const centre = box.left + box.width / 2;
    let best = null;
    let bestDistance = Infinity;

    this.facades.forEach((facade) => {
      const rect = facade.getBoundingClientRect();
      if (rect.right <= box.left || rect.left >= box.right) return;

      const distance = Math.abs(rect.left + rect.width / 2 - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = facade;
      }
    });

    return best;
  }

  syncAutoplay() {
    if (!this.autoplayEnabled) {
      this.unmountAmbient();
      return;
    }

    /*
      A card the visitor started by hand keeps the page while it is mounted.
      There is no way to know when a cross-origin embed has finished, so "while
      it is playing" is "while its iframe exists"; scrolling it off the track,
      leaving the section, or another section claiming the registry all end it,
      and autoplay resumes from there.
    */
    if (this.deliberate?.querySelector('iframe')) return;

    const target = this.activeFacade();
    if (!target) {
      this.unmountAmbient();
      return;
    }

    if (this.ambient === target && target.querySelector('iframe')) return;

    this.mountFacade(target, true);
  }

  unmountAmbient() {
    if (this.ambient) this.unmountFacade(this.ambient);
  }

  /*
    Scroll and resize are debounced because both fire continuously, and every
    call that got through would tear down one third-party player and build
    another. The arrows are bound as well as the scroll they cause: Dawn scrolls
    the track with behavior smooth, and the scroll events that produces stop
    arriving the moment the animation settles, so relying on them alone leaves
    the decision on whichever frame happened to be last.
  */
  bindAutoplayTriggers() {
    if (this.facades.length === 0) return;

    let timer = null;
    this.queueSync = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => this.syncAutoplay(), 150);
    };

    this.track?.addEventListener('scroll', this.queueSync, { passive: true });
    window.addEventListener('resize', this.queueSync);
    this.querySelectorAll('button[name="previous"], button[name="next"]').forEach((button) => {
      button.addEventListener('click', this.queueSync);
    });
  }

  /*
    Native <video> cards only.

    This used to sit inline in connectedCallback and bail out of the whole
    method when the section had no <video> elements - which is the shipped
    configuration, where every card is an external video. Everything below that
    line was therefore never reached: the visibilitychange listener and the
    reduced-motion listener were silently not attached. It happened to cost
    nothing, because both only pause <video> elements and there were none, but
    anything added after that line would have quietly not run.

    So the early return lives here now, where the only thing it skips is the
    native-video handling it belongs to. this.videos is still assigned either
    way, so the handlers in connectedCallback can iterate it without a guard.
  */
  setupNativeVideos() {
    this.videos = Array.from(this.querySelectorAll('.editorial-carousel__card video'));
    if (this.videos.length === 0) return;
    if (!('IntersectionObserver' in window)) return;

    /*
      The root is the scrolling track, not the viewport. A card can be well
      inside the page and still be scrolled off the side of the carousel, and
      observing against the viewport would leave those playing.
    */
    const root = this.querySelector('[id^="Slider-"]');
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video');
          if (!video) return;
          if (entry.isIntersecting) this.play(video);
          else video.pause();
        });
      },
      { root, threshold: 0.5 }
    );
    this.querySelectorAll('.editorial-carousel__card').forEach((card) => this.observer.observe(card));
  }

  /*
    Viewport-rooted, alongside the track-rooted observers rather than instead of
    them. Those answer "is this card scrolled off the side of the carousel",
    and they cannot answer "is the carousel still on screen": their root is the
    track itself, so a card sitting in the middle of it keeps intersecting no
    matter how far down the page the visitor has scrolled.

    Leaving the viewport tears everything down, ambient and deliberate alike.
    Coming back starts the centred card again but does not resume a deliberate
    watch: autoplay is the section's own behaviour and belongs to wherever the
    visitor is looking, while a card somebody chose to watch with sound should
    not restart itself because they scrolled past twice.

    This is also what keeps inViewport honest, which autoplayEnabled reads.
  */
  watchViewport() {
    if (!('IntersectionObserver' in window)) return;

    this.viewportObserver = new IntersectionObserver(
      (entries) => {
        this.inViewport = entries[0].isIntersecting;

        if (!this.inViewport) {
          this.facades.forEach((facade) => this.unmountFacade(facade));
          this.videos?.forEach((video) => video.pause());
          return;
        }

        this.syncAutoplay();
      },
      { threshold: 0 }
    );

    this.viewportObserver.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.facadeObserver?.disconnect();
    this.viewportObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.reducedMotion?.removeEventListener('change', this.onMotionChange);
    if (this.queueSync) {
      this.track?.removeEventListener('scroll', this.queueSync);
      window.removeEventListener('resize', this.queueSync);
    }
  }

  play(video) {
    if (this.reducedMotion.matches || document.hidden) return;
    const played = video.play();
    if (played) played.catch(() => {});
  }

  /* External video facades ------------------------------------------------ */

  /*
    A facade is a poster and a button. The iframe is created on activation and
    destroyed again when the card leaves the track or another card's video is
    started, so at most one third-party player exists at any moment and none
    exists until somebody asks for one.

    Reduced motion is consulted by autoplay and deliberately not by the button.
    A visitor who presses play has asked for the video, and refusing that would
    be worse than the motion it avoids; a card that starts on its own has not
    been asked for, so it does not start at all under reduced motion.
  */
  setupFacades() {
    this.facades = Array.from(this.querySelectorAll('[data-facade]'));
    if (this.facades.length === 0) return;

    this.facades.forEach((facade) => {
      /*
        The play button is a deliberate watch even on the card autoplay already
        chose: same card, different playback. Unmounting first is what makes
        that swap happen, because mountFacade returns early on a facade that
        already holds an iframe.
      */
      facade.querySelector('[data-facade-play]')?.addEventListener('click', () => {
        this.unmountFacade(facade);
        this.mountFacade(facade, false);
      });
      this.watchPoster(facade);
    });

    /*
      Unmount on leaving the track. The root is the scrolling track rather than
      the viewport: a card can be well inside the page and still scrolled off
      the side of the carousel, and a player left running there is audible with
      nothing visible to explain it.
    */
    if ('IntersectionObserver' in window) {
      const root = this.querySelector('[id^="Slider-"]');
      this.facadeObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) this.unmountFacade(entry.target);
          });
        },
        { root, threshold: 0.25 }
      );
      this.facades.forEach((facade) => this.facadeObserver.observe(facade));
    }
  }

  /*
    maxresdefault is not generated for every video. Depending on the video it
    either 404s or returns a 120px grey placeholder, so both are checked: the
    error event covers the first, the natural width covers the second.
  */
  watchPoster(facade) {
    const poster = facade.querySelector('[data-poster-fallback]');
    if (!poster) return;

    const swap = () => {
      const fallback = poster.dataset.posterFallback;
      if (!fallback || poster.src === fallback) return;
      poster.src = fallback;
      /*
        hqdefault is 4:3 with YouTube's black bands baked into the pixels, so it
        has to be cropped rather than contained or the bands show and the card
        reads smaller than its neighbours. The class is added here rather than in
        Liquid because only the component knows the swap happened.
      */
      poster.classList.add('editorial-carousel__poster--cropped');
    };

    poster.addEventListener('error', swap, { once: true });
    poster.addEventListener('load', () => {
      if (poster.naturalWidth > 0 && poster.naturalWidth <= 120) swap();
    });
  }

  /*
    `ambient` picks the muted looping URL and records the card as autoplay's;
    anything else is a deliberate watch and records it as the visitor's. A
    facade is only ever one or the other, so the flags are cleared for the other
    role on the way in.
  */
  mountFacade(facade, ambient = false) {
    if (facade.querySelector('iframe')) return;

    /*
      One at a time, page-wide rather than section-wide. The registry tears
      down whichever player held the page before this one, which may be another
      card here or the hero's slide embed. Claiming before the iframe is built
      means the old player is already gone by the time the new one starts, so
      there is never a frame with two of them loading.

      This is also how the two sections hand over without either knowing about
      the other: the hero claiming evicts our card through the unmount callback
      below, which clears whichever flag held it, and the next scroll or
      viewport callback re-claims from a clean state.
    */
    window.themeVideoRegistry?.claim(facade, () => this.unmountFacade(facade));

    if (ambient) {
      this.ambient = facade;
      if (this.deliberate === facade) this.deliberate = null;
    } else {
      this.deliberate = facade;
      if (this.ambient === facade) this.ambient = null;
    }

    const iframe = document.createElement('iframe');
    iframe.className = 'editorial-carousel__embed';
    /*
      dataset.embedUrlAmbient is only absent if the section was rendered before
      this attribute existed, in which case falling back to the deliberate URL
      is better than mounting nothing.
    */
    iframe.src = ambient ? facade.dataset.embedUrlAmbient || facade.dataset.embedUrl : facade.dataset.embedUrl;
    iframe.title = facade.dataset.embedTitle || '';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('frameborder', '0');
    facade.appendChild(iframe);
    facade.classList.add('editorial-carousel__facade--playing');
    facade.classList.toggle('editorial-carousel__facade--ambient', ambient);
  }

  unmountFacade(facade) {
    /*
      The flags are cleared before the early return, not after it. This is
      called on facades that hold no iframe - the viewport observer sweeps all
      of them - and a stale flag left pointing at a torn-down card would make
      syncAutoplay believe a deliberate watch was still running and refuse to
      autoplay anything again.
    */
    if (this.ambient === facade) this.ambient = null;
    if (this.deliberate === facade) this.deliberate = null;

    const iframe = facade.querySelector('iframe');
    if (!iframe) return;
    iframe.remove();
    facade.classList.remove('editorial-carousel__facade--playing');
    facade.classList.remove('editorial-carousel__facade--ambient');
    window.themeVideoRegistry?.release(facade);
  }
}

customElements.define('editorial-carousel', EditorialCarousel);
